import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authed } from '../../lib/api';
import { formatMoney } from '../../lib/utils';
import { useAdmin } from '../../lib/auth';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { OrderDetailsModal } from '../../components/OrderDetailsModal';
import { OrderStatusBadge, PaymentStatusBadge, isOverdueCollection, formatOverdueAge } from '../../components/StatusBadges';
import { ArrowLeft, ShoppingBag, Eye, AlertTriangle } from 'lucide-react';

/**
 * Item 4 + Batch B2 Item 12 — Employee Admin "Check Orders" view.
 *
 * View-only orders table. Employee cannot edit, cancel, delete, or modify
 * anything. Row → full popup with order details (address, distributor,
 * ordered products, totals) but no status editor.
 */
export default function AdminOrdersOnly() {
    const nav = useNavigate();
    const { admin } = useAdmin();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [paymentFilter, setPaymentFilter] = useState('all');
    const [detailId, setDetailId] = useState(null);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (paymentFilter && paymentFilter !== 'all') params.set('payment_status', paymentFilter);
        authed('admin', { method: 'GET', url: `/admin/orders?${params.toString()}` })
            .then(r => setRows(r.data || []))
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [statusFilter, paymentFilter]);

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6 py-6">
                <div className="flex items-center gap-2 mb-3">
                    <button onClick={() => nav('/')} className="p-2 rounded-full hover:bg-black/5" aria-label="Back" data-testid="check-orders-back"><ArrowLeft size={18} /></button>
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-sdf-text uppercase tracking-tight">Check Orders</h1>
                </div>
                <p className="text-xs text-sdf-text-2 mb-4">View-only mode. Signed in as <b>{admin?.email}</b>.</p>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2 mr-1">Status</span>
                    {['all', 'placed', 'confirmed', 'dispatched', 'delivered', 'cancelled'].map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === s ? 'bg-sdf-red text-white border-sdf-red' : 'bg-white border-sdf-line hover:bg-neutral-50'}`}
                            data-testid={`check-orders-filter-${s}`}
                        >
                            {s.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2 mr-1">Payment</span>
                    {['all', 'pending', 'paid'].map(p => (
                        <button
                            key={p}
                            onClick={() => setPaymentFilter(p)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                paymentFilter === p
                                    ? (p === 'pending' ? 'bg-amber-500 text-white border-amber-500' : p === 'paid' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-sdf-text text-white border-sdf-text')
                                    : 'bg-white border-sdf-line hover:bg-neutral-50'
                            }`}
                            data-testid={`check-orders-payment-filter-${p}`}
                        >
                            {p.toUpperCase()}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <div className="py-10 text-center text-sdf-text-2">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="py-14 text-center text-sm text-sdf-text-2">
                        <ShoppingBag size={40} className="mx-auto text-neutral-300" />
                        <div className="mt-3 font-bold text-sdf-text">No orders found</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto card-soft">
                        <table className="min-w-full text-sm">
                            <thead className="bg-white/60 text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">
                                <tr>
                                    <th className="px-3 py-2 text-left">Order #</th>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Distributor</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-left">Payment</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(o => {
                                    const overdue = isOverdueCollection(o);
                                    const age = overdue ? formatOverdueAge(o) : null;
                                    return (
                                    <tr
                                        key={o.id}
                                        className={`border-t border-sdf-line hover:bg-sdf-cream/30 transition-colors ${overdue ? 'bg-amber-50/70 border-l-4 border-l-amber-500' : ''}`}
                                        data-testid={`check-orders-row-${o.id}`}
                                    >
                                        <td className="px-3 py-2 font-mono text-xs">
                                            {overdue ? (
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="inline-flex items-center gap-1.5" title={`Credit order overdue · ${age}`}>
                                                        <AlertTriangle
                                                            size={13}
                                                            className="text-amber-600 shrink-0"
                                                            aria-label="Payment overdue"
                                                            data-testid={`check-orders-overdue-${o.id}`}
                                                        />
                                                        <span>{o.order_number}</span>
                                                    </span>
                                                    {age && (
                                                        <span
                                                            className="inline-flex items-center px-1.5 py-0 text-[9px] font-bold uppercase tracking-widest rounded-full border bg-amber-100 text-amber-900 border-amber-300 w-fit"
                                                            data-testid={`check-orders-overdue-age-${o.id}`}
                                                        >
                                                            {age} overdue
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span>{o.order_number}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-sdf-text-2 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-xs">{o.distributor_mobile || o.address?.phone || '—'}</td>
                                        <td className="px-3 py-2">
                                            <OrderStatusBadge status={o.status} testid={`check-orders-status-${o.id}`} />
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                                            <span className="font-bold">{(o.payment_method || '').toUpperCase() || '—'}</span>
                                            <span className="mx-1 text-sdf-text-2">·</span>
                                            <PaymentStatusBadge status={o.payment_status} testid={`check-orders-payment-${o.id}`} />
                                        </td>
                                        <td className="px-3 py-2 text-right font-extrabold">{formatMoney(o.total)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                onClick={() => setDetailId(o.id)}
                                                className="btn-outline text-xs inline-flex"
                                                data-testid={`check-orders-view-${o.id}`}
                                            >
                                                <Eye size={12} /> View
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {detailId && (
                    <OrderDetailsModal
                        orderId={detailId}
                        realm="admin"
                        canEdit={true}
                        onClose={() => setDetailId(null)}
                        onStatusChanged={load}
                    />
                )}
            </main>
            <Footer />
        </div>
    );
}
